import { createSelector } from 'reselect'
import { getPageData, getPageParams } from 'selectors/page'
import { getSort, getHitsPerPage, getPage, getSearch } from 'selectors/algolia2/url'
import {
  getIsAdmin,
  getUserID,
  isAuthenticated as getIsAuthenticated,
} from 'selectors/user/session'
import { capitalizeString } from 'lib/helpers'
import { getSelectedFacets } from 'selectors/searchProvider'
import { getFullName, getAccountUserID } from 'apps/public/profile/artistProfile/selectors'

// We want a list of all selected facets => [{ facetName: string, attributeName: string }, ...]
type SelectedFacetTransformed = {
  facetName: string
  attributeName: string | ReadonlyArray<string>
  title: string
}
export const getAllSelectedFacets = createSelector(
  [getSelectedFacets],
  (selectedFacets): ReadonlyArray<SelectedFacetTransformed> => {
    const selectedData: Array<SelectedFacetTransformed> = []

    Object.entries(selectedFacets).forEach(([facetName, attributes]) => {
      if (Array.isArray(attributes)) {
        attributes.forEach((attributeName) => {
          selectedData.push({
            facetName,
            attributeName,
            title: String(attributeName),
          })
        })
      } else {
        selectedData.push({
          facetName,
          attributeName: attributes,
          title: String(attributes),
        })
      }
    })

    return selectedData
  }
)
export const getSelectedFacetsCount = createSelector(
  [getAllSelectedFacets],
  (allSelectedFacets): number => {
    const pageNumber = allSelectedFacets.some((e) => e.facetName === 'page')
    const hitsPerPage = allSelectedFacets.some((e) => e.facetName === 'hitsPerPage')

    const isPageFacet = () => {
      if (pageNumber && hitsPerPage) return 2
      else if (pageNumber || hitsPerPage) return 1
      else return 0
    }

    const selectedFacetsLength = allSelectedFacets.length - isPageFacet()
    return selectedFacetsLength
  }
)

// This is the order of params from Express Router
// Example: `:category/:subject/:styles`
export const getPathParams = createSelector(
  getPageData,
  (pageData): Array<string> => {
    return pageData.pathParams || []
  }
)
// Special Query Fields we will USE
export const getDimensionQuery = createSelector(
  [getPageParams],
  ({ width, height }): Record<string, any> => ({
    ...(width
      ? {
          width,
        }
      : {}),
    ...(height
      ? {
          height,
        }
      : {}),
  })
)
export const getPageParamsQuery = createSelector(
  [getSort, getHitsPerPage, getPage, getSearch, getDimensionQuery],
  (sort, hitsPerPage, page, query, diminsions): Record<string, any> => ({
    ...(sort !== 'default'
      ? {
          sort,
        }
      : {}),
    ...(hitsPerPage !== 25
      ? {
          hitsPerPage,
        }
      : {}),
    ...(page !== 0
      ? {
          page: page + 1,
        }
      : {}),
    ...(query
      ? {
          query,
        }
      : {
          query: '',
        }),
    ...diminsions,
  })
)
export { getIsAdmin } from 'selectors/user/session'
export { getPageIsReady, getPageData, getPageParams, getPagePath } from 'selectors/page'
export const getArtistId = createSelector([getPageData], (pageData): number => pageData.userId || 0)
export const getArtistCountry = createSelector(
  [getPageData],
  (pageData): number => pageData.country || ''
)

/* export const getSearch = createSelector(
  getPageParams,
  (pageParams): Object => pageParams.search || ''
) */
export const areFiltersAvailable = createSelector(
  [getPageData],
  (pageData): boolean => pageData.filtersAvailable || false
)
export const getIsUsersCollection = createSelector(
  [getPageParams, getUserID, getIsAuthenticated],
  (params, userId, isAuthenticated): boolean =>
    (isAuthenticated && Number(userId) === Number(params.ownerId || 0)) || false
)
export const getIsUsersArtworksAccount = createSelector(
  [getPageParams, getUserID, getIsAuthenticated],
  (params, userId, isAuthenticated): boolean =>
    (isAuthenticated && Number(userId) === Number(params.userId || 0)) || false
)
export const getPageParamsUserId = createSelector(
  [getPageParams],
  (params): string => params.userId
)
// Private Collection, we do NOT return algolia results from SSR
export const getHasInitialLoad = createSelector(
  [getPageData],
  (pageData): boolean => pageData.algoliaResults || false
)
export const getHasValidPermissions = createSelector(
  [getIsUsersArtworksAccount, getIsAdmin],
  (isUsersArtworksAccount, isAdmin): boolean => isUsersArtworksAccount || isAdmin || false
)
export const getBreadcrumbSchema = createSelector(
  [getAccountUserID, getFullName],
  (artistUserId, artistName) => {
    if (!process.env.SITE_URL) {
      throw new Error('Please define SITE_URL in .env')
    }

    const siteUrl = process.env.SITE_URL
    const capitalizedArtistName = capitalizeString(artistName)

    return {
      '@context': 'http://schema.org',
      type: 'BreadcrumbList',
      itemListElement: [
        {
          type: 'ListItem',
          position: 1,
          item: {
            '@id': `${siteUrl}/account/profile/${artistUserId}`,
            name: `${capitalizedArtistName} Artist Profile`,
          },
        },
        {
          type: 'ListItem',
          position: 2,
          item: {
            '@id': `${siteUrl}/account/artworks/${artistUserId}`,
            name: `${capitalizedArtistName} Artworks`,
          },
        },
      ],
    }
  }
)
